import * as React from "react"

/**
 * Modal Portal Context
 *
 * This context allows popovers, selects, and dropdown menus to render inside
 * their parent modal instead of to document.body. This fixes the issue where
 * dropdowns appear clipped or hidden when used inside modals due to overflow
 * and stacking context issues.
 *
 * Usage:
 * 1. Wrap modal content (Dialog, Sheet) with ModalPortalProvider
 * 2. Use useModalPortalContainer() in portaled components to get the container
 * 3. Pass the container to Radix Portal's container prop
 */

const ModalPortalContext = React.createContext<HTMLElement | null>(null)

interface ModalPortalProviderProps {
  children: React.ReactNode
}

/**
 * Provider that creates a portal container for nested popovers/dropdowns.
 * Wrap modal content with this to enable proper dropdown rendering inside modals.
 */
export function ModalPortalProvider({ children }: ModalPortalProviderProps) {
  const [container, setContainer] = React.useState<HTMLElement | null>(null)

  return (
    <ModalPortalContext.Provider value={container}>
      <div ref={setContainer} className="contents">
        {children}
      </div>
    </ModalPortalContext.Provider>
  )
}

/**
 * Hook to get the modal portal container.
 * Returns undefined when outside a modal (Radix will use document.body).
 * Returns the modal's container element when inside a modal.
 */
export function useModalPortalContainer(): HTMLElement | undefined {
  const container = React.useContext(ModalPortalContext)
  // Return undefined (not null) when outside modal - this tells Radix to use document.body
  return container || undefined
}
